import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SUBMIT_SCENARIO } from "@/constants/testIds";
import { ViolentIncidentWarning } from "@/components/scenarios/ViolentIncidentWarning";
import { ScenarioDetailsForm } from "@/components/scenarios/ScenarioDetailsForm";
import { useScenarioSubmit } from "@/hooks/useScenarioSubmit";

const CATEGORIES = [
  { key: "expression", label: "Disrespect & Dismissal", desc: "Words, tone, or attitude that show bias." },
  { key: "behavior_nonviolent", label: "Unfair Treatment", desc: "Bias that changed how you were actually treated." },
  { key: "behavior_violent", label: "Threats or Violence", desc: "This is a safety emergency, not a vote." },
];

export default function SubmitScenario() {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("");
  const [mediaFile, setMediaFile] = useState(null);
  const navigate = useNavigate();
  const { submitScenario, loading, error, submitted, submitLabel } = useScenarioSubmit();

  const isViolent = category === "behavior_violent";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isViolent) return;
    const ok = await submitScenario({ title, content, category, mediaFile });
    if (ok) setTimeout(() => navigate("/feed"), 1200);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A6E] mb-2">Tell the Story</p>
      <h1 className="font-display font-extrabold uppercase text-3xl md:text-4xl text-zinc-950 tracking-tight mb-2">Share a Scenario</h1>
      <p className="text-zinc-500 text-sm mb-8">Use plain language. The community will read this and vote.</p>

      {submitted ? (
        <div data-testid="submit-scenario-success-message" className="p-6 rounded-sm bg-[#4FD1C5]/10 border border-[#4FD1C5]/40 text-[#1E3A6E] text-center">
          Thank you. Your scenario is now live on the voting feed.
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white border border-zinc-200 rounded-sm p-6 sm:p-8 space-y-6">
          {error && <div className="p-3 rounded-sm bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

          <div>
            <Label className="text-zinc-500 text-xs uppercase tracking-wide mb-2 block">What kind of bias was this?</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid={SUBMIT_SCENARIO.categorySelect} className="bg-white border-zinc-300 text-zinc-950 rounded-none focus:ring-1 focus:ring-[#4FD1C5]">
                <SelectValue placeholder="Choose a category" />
              </SelectTrigger>
              <SelectContent className="bg-white border-zinc-200 text-zinc-950">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.key} value={c.key} data-testid={SUBMIT_SCENARIO.categoryOption(c.key)}>
                    <div>
                      <p className="font-medium">{c.label}</p>
                      <p className="text-xs text-zinc-500">{c.desc}</p>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isViolent ? (
            <ViolentIncidentWarning onGoToBern={() => navigate("/bern")} />
          ) : (
            <ScenarioDetailsForm
              title={title} content={content} mediaFile={mediaFile} submitLabel={submitLabel} loading={loading}
              onTitleChange={setTitle} onContentChange={setContent} onFileChange={setMediaFile}
            />
          )}
        </form>
      )}
    </div>
  );
}
